import upperBleph from "@/assets/results/upper-bleph.png";
import upperBleph2 from "@/assets/results/upper-bleph-2.png";
import upperBleph3 from "@/assets/results/upper-bleph-3.png";
import upperBleph4 from "@/assets/results/upper-bleph-4.png";
import upperBleph5 from "@/assets/results/upper-bleph-5.png";
import lowerBleph1 from "@/assets/results/lower-bleph-1.png";
import lowerBleph2 from "@/assets/results/lower-bleph-2.png";
import lowerBleph3 from "@/assets/results/lower-bleph-3.png";

type Result = {
  src: string;
  alt: string;
  procedure: string;
  caption: string;
  detail: string;
  layout: "side-by-side" | "stacked";
};

const results: Result[] = [
  {
    src: upperBleph,
    alt: "Before and after photographs of upper eyelid blepharoplasty by Dr Vahe — hooded lids refined to a lifted, rested appearance",
    procedure: "Upper Blepharoplasty",
    caption: "Lifted, awake, unmistakably her.",
    detail:
      "Excess upper-lid skin removed through a hidden crease incision — the eye opens without ever looking 'done'.",
    layout: "stacked",
  },
  {
    src: upperBleph2,
    alt: "Before and after photographs of upper eyelid blepharoplasty by Dr Vahe — heavy crepey upper lids refined and brightened",
    procedure: "Upper Blepharoplasty",
    caption: "Years, gently set down.",
    detail:
      "Crepey, heavy upper-lid skin softened — the lash line reappears and the eye opens to its natural shape.",
    layout: "stacked",
  },
  {
    src: upperBleph3,
    alt: "Before and after photographs of upper eyelid blepharoplasty by Dr Vahe — hooded lids opened to reveal a softer, more rested gaze",
    procedure: "Upper Blepharoplasty",
    caption: "The gaze, returned.",
    detail:
      "Hooding refined through a discreet crease incision — definition restored without altering character.",
    layout: "stacked",
  },
  {
    src: upperBleph4,
    alt: "Before and after photographs of upper eyelid blepharoplasty by Dr Vahe — tired, heavy upper lids refreshed to a brighter, open eye",
    procedure: "Upper Blepharoplasty",
    caption: "Brighter, by design.",
    detail:
      "Conservative skin removal with careful preservation of the natural lid platform — the eye reads open and unhurried.",
    layout: "stacked",
  },
  {
    src: upperBleph5,
    alt: "Before and after photographs of upper eyelid blepharoplasty by Dr Vahe — softened upper-lid hooding for a more youthful, awake appearance",
    procedure: "Upper Blepharoplasty",
    caption: "Softly, awake.",
    detail:
      "Subtle refinement of the upper-lid fold — character preserved, freshness returned.",
    layout: "stacked",
  },
  {
    src: lowerBleph1,
    alt: "Before and after photographs of lower eyelid blepharoplasty by Dr Vahe — under-eye hollows and bags smoothed for a rested look",
    procedure: "Lower Blepharoplasty",
    caption: "Rested, not retouched.",
    detail:
      "Trans-conjunctival approach with fat repositioning — under-eye shadows and bags resolved without a visible scar.",
    layout: "side-by-side",
  },
  {
    src: lowerBleph2,
    alt: "Before and after photographs of lower eyelid surgery by Dr Vahe — puffy lower lids smoothed and brightened",
    procedure: "Lower Blepharoplasty",
    caption: "The shadow, lifted.",
    detail:
      "Fine refinement of the lid–cheek junction restores a smooth, even contour — the eye reads bright again.",
    layout: "stacked",
  },
  {
    src: lowerBleph3,
    alt: "Before and after photographs of lower eyelid blepharoplasty by Dr Vahe — deep under-eye bags and festoons smoothed for a refreshed contour",
    procedure: "Lower Blepharoplasty",
    caption: "The weight, released.",
    detail:
      "Pronounced lower-lid bags and crepey skin addressed in a single sitting — the cheek-lid junction restored to a calm, even line.",
    layout: "side-by-side",
  },
];

const ResultsGallery = () => {
  return (
    <section
      id="results"
      className="py-24 md:py-32 px-6 md:px-10 bg-secondary/40 border-y border-border/40"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl mb-16 md:mb-20">
          <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
            Before &amp; After
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mt-6 text-balance">
            Real results, <span className="italic">quietly</span> transformative.
          </h2>
          <p className="text-muted-foreground font-light leading-relaxed mt-6 max-w-lg">
            A small selection of Dr Vahe's blepharoplasty work. Patient privacy
            is paramount — every image is shared with written consent.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {results.map((r, idx) => (
            <figure
              key={idx}
              className="group relative overflow-hidden rounded-[28px] md:rounded-[36px] bg-background shadow-soft border border-border/40"
            >
              <div
                className={
                  r.layout === "side-by-side"
                    ? "aspect-[16/10] overflow-hidden bg-secondary"
                    : "aspect-[4/5] overflow-hidden bg-secondary"
                }
              >
                <img
                  src={r.src}
                  alt={r.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="p-6 md:p-7">
                <span className="text-[10px] tracking-[0.3em] uppercase text-accent font-medium">
                  {r.procedure}
                </span>
                <p className="font-display text-2xl md:text-[26px] text-foreground mt-2">
                  {r.caption}
                </p>
                <p className="text-sm text-muted-foreground font-light leading-relaxed mt-3">
                  {r.detail}
                </p>
              </figcaption>
            </figure>
          ))}
        </div>

        <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground/80 mt-10 md:mt-12 text-center">
          Individual results vary · Shared with patient consent
        </p>
      </div>
    </section>
  );
};

export default ResultsGallery;
